import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCollaboration } from '@/hooks/useCollaborations';
import { useSetHumanScore } from '@/hooks/useEndpointTestRunGroups';
import { useToast } from '@/contexts/ToastContext';
import { useFlag } from '@/hooks/useFeatureFlags';
import { UserName } from '@/components/ui/UserName';
import { formatRelativeTime } from '@/utils/dateUtils';
import { humanScoreOf, resultDisplay, resultRevision, servedVersionLabel } from '@/utils/testRunResults';
import { ScoresTable } from './ScoresTable';
import { CriteriaEditor } from './CriteriaEditor';
import type { EndpointTestRunResult } from '@/types/endpointTests';
import { serviceRunPath } from '@/components/runExplorer/runExplorerLinks';

interface TestResultDrawerProps {
  result: EndpointTestRunResult;
  endpointId: string;
  endpointTestRunGroupId: string;
  /** The version the test was stamped with (task 1237); null for the working state. */
  testVersion?: { major: number; minor: number } | null;
}

const preClass =
  'p-2 bg-gray-50 dark:bg-gray-900 rounded text-xs font-mono text-gray-800 dark:text-gray-200 overflow-auto max-h-48 whitespace-pre-wrap break-words';

/**
 * The result drawer (task 1177): the input sent, the output received, the
 * human verdict, and the link to the full collaboration run. The verdict is the
 * result's own `human` score — never overwritten by a grader — and the strip
 * and row re-read it on save.
 */
export function TestResultDrawer({ result, endpointId, endpointTestRunGroupId, testVersion }: TestResultDrawerProps) {
  const [editingCriteria, setEditingCriteria] = useState(false);
  const disagreement = resultDisplay(result).note;
  const { data: collaboration, isLoading } = useCollaboration(result.collaborationId ?? undefined);
  const existing = humanScoreOf(result);
  const [outcome, setOutcome] = useState<'pass' | 'fail' | null>(
    existing?.outcome === 'pass' || existing?.outcome === 'fail' ? existing.outcome : null,
  );
  const [note, setNote] = useState(existing?.rationale ?? '');
  const setHumanScore = useSetHumanScore();
  const toast = useToast();
  // Two levers (task 1215): the scores table and this scenario's criteria are
  // the criteria feature set; the human verdict belongs to test runs.
  const criteriaEnabled = useFlag('innovate.fe.testing.criteria');
  const runsEnabled = useFlag('innovate.fe.testing.runs');

  const input = result.endpointTest?.input ?? collaboration?.input ?? null;
  /**
   * What actually served this result (task 1237): the collaboration's own
   * stamp, beside the version the test started against. They differ when a
   * release landed mid-run, and the drawer says so rather than letting the
   * test's label speak for this result.
   */
  const served = collaboration ? servedVersionLabel(collaboration.endpointVersion) : undefined;
  const stamped = testVersion ? `v${testVersion.major}.${testVersion.minor}` : null;
  const versionMismatch = served !== undefined && collaboration?.status !== 'pending' && served !== stamped;
  const output = collaboration?.output ?? null;

  const handleSave = async () => {
    if (!outcome) return;
    try {
      await setHumanScore.mutateAsync({
        endpointTestRunId: result.id,
        endpointTestRunGroupId,
        endpointId,
        data: { outcome, ...(note.trim() ? { rationale: note.trim() } : {}) },
      });
      toast.success('Verdict saved', `human ${outcome}`);
    } catch (err) {
      toast.error('Failed to save verdict', err instanceof Error ? err.message : undefined);
    }
  };

  const segment = (value: 'pass' | 'fail', label: string) => (
    <button
      type="button"
      aria-pressed={outcome === value}
      onClick={() => setOutcome(value)}
      className={`px-3 py-1 text-xs font-medium border first:rounded-l-md last:rounded-r-md -ml-px first:ml-0 ${
        outcome === value
          ? value === 'pass'
            ? 'bg-green-600 border-green-600 text-white'
            : 'bg-red-600 border-red-600 text-white'
          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      data-testid={`result-drawer-${result.id}`}
      className="pl-7 pr-3 py-3 space-y-3 border-t border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Input</p>
          <pre className={preClass}>{input ? JSON.stringify(input, null, 2) : '—'}</pre>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Output</p>
          <pre className={preClass}>
            {isLoading ? 'Loading…' : output ? JSON.stringify(output, null, 2) : '—'}
          </pre>
        </div>
      </div>

      {served !== undefined && (
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="result-served-version">
          Served by <span className="font-mono">{served ?? 'the working state'}</span>
          {' · '}test started against <span className="font-mono">{stamped ?? 'the working state'}</span>
          {versionMismatch && (
            <span
              data-testid="result-version-mismatch"
              title="This result was served by a different version than the test started against"
              className="ml-2 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
            >
              different version
            </span>
          )}
        </p>
      )}

      {/* The scores table (task 1180): every check, graded criterion and the
          human verdict. Renders nothing for a Phase 1 result with no scores. */}
      {criteriaEnabled && <ScoresTable endpointTestRunId={result.id} revision={resultRevision(result)} />}
      {criteriaEnabled && disagreement && (
        <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="verdict-disagreement">
          The human verdict disagrees with the grader ({disagreement}); the human verdict is what this result shows.
        </p>
      )}

      {/* Edit mode: this test's own criteria, edited in place. */}
      {criteriaEnabled && (
        <div>
          <button
            type="button"
            onClick={() => setEditingCriteria((value) => !value)}
            aria-expanded={editingCriteria}
            className="text-xs font-medium text-indigo-600 dark:text-indigo-400"
          >
            {editingCriteria ? 'Done editing criteria' : 'Edit this scenario’s criteria'}
          </button>
          {editingCriteria && (
            <div className="mt-2">
              <CriteriaEditor scope={{ kind: 'test', endpointTestId: result.endpointTestId }} />
            </div>
          )}
        </div>
      )}

      {runsEnabled && (
        <div className="space-y-2" data-testid="human-verdict">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Human verdict</span>
            <span className="inline-flex" role="group" aria-label="Human verdict">
              {segment('pass', 'Pass')}
              {segment('fail', 'Fail')}
            </span>
            {existing && (
              <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="human-verdict-attribution">
                <UserName userId={existing.createdBy} /> · {formatRelativeTime(existing.createdAt)}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-start gap-2">
            <label htmlFor={`verdict-note-${result.id}`} className="sr-only">Note</label>
            <textarea
              id={`verdict-note-${result.id}`}
              aria-label="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={1}
              placeholder="Optional note"
              className="flex-1 min-w-[12rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-900 dark:text-white"
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={!outcome || setHumanScore.isPending}
              className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {setHumanScore.isPending ? 'Saving…' : 'Save verdict'}
            </button>
          </div>
        </div>
      )}

      {result.collaborationId && (
        <Link
          to={serviceRunPath(endpointId, result.collaborationId)}
          className="inline-block text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          Open the full collaboration run →
        </Link>
      )}
    </div>
  );
}

export default TestResultDrawer;
